import React, { useMemo } from "react";
import { Column, usePagination, useSortBy, useTable } from "react-table";
import { ObjectPanelTableProps } from "../../../../types/ObjectBrowser/ComponentTypes";
import ObjectDetailsType from "../../../../types/ObjectBrowser/ObjectDetailsType";

// Components
import ReactTable from "../../../../component/ReactTable";
import './style.scss';

const ObjectPanelTable: React.FC<ObjectPanelTableProps> = ({ instance, selectedItem, optionsList, loading, setSelectedObject }) => {
    const objectType = selectedItem?.objectType;

    const onRowClick = (row: any) => {
        setSelectedObject({
            ...row,
            instanceId: instance.id,
        });
    };

    const filteredData = useMemo(() => {
        return optionsList.map((row: any) => {
            const type = row.type ?? 'Table'; // fallback if `type` is missing

            if (type === 'Table') {
                return {
                    tablename: row.tablename,
                    tableschema: row.tableschema,
                    database: row.database,
                    objectType: type,
                    rows: row.rows,
                    tablesizebytes: row.tablesizebytes,
                    indexsizebytes: row.indexsizebytes,
                } as ObjectDetailsType;
            } else {
                return {
                    tablename: row.tablename,
                    indexname: row.indexname,
                    tableschema: row.indexschema,
                    indexschema: row.indexschema,
                    database: row.database,
                    objectType: type,
                    rows: null,
                    tablesizebytes: row.tablesizebytes,
                    indexsizebytes: row.indexsizebytes,
                } as ObjectDetailsType;
            }
        });
    }, [optionsList]);


    const columns = useMemo<Column[]>(() => [
        {
            Header: objectType === "Table" ? "Table" : "Index",
            accessor: objectType === "Table" ? 'tablename' : 'indexname',
            Cell: ({ row, value }: { row: any, value: string }) => (
                    <>
                        {row.original.objectType === "Table" && <i className="fa fa-table me-1" aria-hidden="true"/>}
                        {row.original.objectType === "Index" && <i className="fa fa-list-ol me-1" aria-hidden="true"/>}
                        <span className='panel-link' onClick={() => onRowClick(row.original)}>
                            {value}
                    </span>
                    </>
            ),
        },
        {
            Header: "Schema",
            accessor: "tableschema",
        },
        {
            Header: "Database",
            accessor: "database",
        },
        ...(objectType === "Table" ? [
            {
                Header: "Rows",
                accessor: "rows",
                Cell: ({ value }: { value: number | null }) => value?.toLocaleString() ?? "-",
            },
        ] : []),
        {
            Header: "Size",
            accessor: "tablesizebytes",
            Cell: ({ value }: { value: number | undefined }) => value ? `${(value / 1024 / 1024).toFixed(2)} MB` : "-",
        },
    ], [objectType, optionsList]);

    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        prepareRow,
        page,
        canPreviousPage,
        canNextPage,
        pageOptions,
        pageCount,
        gotoPage,
        nextPage,
        previousPage,
        setPageSize,
        state: { pageIndex, pageSize }
    } = useTable(
            {
                columns,
                data: filteredData,
                initialState: { pageIndex: 0, pageSize: 10 },
            },
            useSortBy,
            usePagination
    );

    return (
            <ReactTable
                getTableProps={getTableProps}
                getTableBodyProps={getTableBodyProps}
                headerGroups={headerGroups}
                prepareRow={prepareRow}
                page={page}
                canPreviousPage={canPreviousPage}
                canNextPage={canNextPage}
                pageOptions={pageOptions}
                pageCount={pageCount}
                gotoPage={gotoPage}
                nextPage={nextPage}
                previousPage={previousPage}
                setPageSize={setPageSize}
                pageIndex={pageIndex}
                pageSize={pageSize}
                dataLength={filteredData.length}
                error={null}
                loading={loading}
            />
    );
};

export default ObjectPanelTable;
